import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, X, ChevronDown, ChevronUp, Star, Tag, IndianRupee, ArrowUpDown, Grid3X3, LayoutList, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ProductCard from '../components/ProductCard';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'name', label: 'Name A → Z' },
];

const PRICE_RANGES = [
  { label: 'Under ₹500', min: 0, max: 500 },
  { label: '₹500 – ₹1,000', min: 500, max: 1000 },
  { label: '₹1,000 – ₹3,000', min: 1000, max: 3000 },
  { label: '₹3,000 – ₹5,000', min: 3000, max: 5000 },
  { label: 'Over ₹5,000', min: 5000, max: null },
];

export default function Products() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filter section collapse state
  const [catOpen, setCatOpen] = useState(true);
  const [priceOpen, setPriceOpen] = useState(true);
  const [sortOpen, setSortOpen] = useState(true);
  const [featuredOpen, setFeaturedOpen] = useState(true);

  // Read from URL
  const category = searchParams.get('category') || 'all';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1');
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';
  const featuredOnly = searchParams.get('featured') === '1';

  // Custom price range inputs
  const [customMin, setCustomMin] = useState(minPrice);
  const [customMax, setCustomMax] = useState(maxPrice);

  useEffect(() => {
    axios.get(`${API}/products/categories`).then(r => setCategories(r.data.categories)).catch(() => {});
  }, []);

  // Scroll to top whenever the page number changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/wishlist`, { withCredentials: true }).then(r => setWishlistIds(r.data.product_ids || [])).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category !== 'all') params.set('category', category);
    if (search) params.set('search', search);
    params.set('sort', sort);
    params.set('page', page);
    params.set('limit', 12);
    if (minPrice) params.set('min_price', minPrice);
    if (maxPrice) params.set('max_price', maxPrice);

    axios.get(`${API}/products?${params.toString()}`).then(r => {
      let items = r.data.products;
      if (featuredOnly) items = items.filter(p => p.is_featured);
      setProducts(items);
      setTotal(featuredOnly ? items.length : r.data.total);
      setPages(r.data.pages);
      setError(null);
    }).catch((err) => {
      console.error("Products fetch error:", err);
      setError(err.message || 'Failed to fetch products');
      setProducts([]);
      setTotal(0);
    }).finally(() => setLoading(false));
  }, [category, search, sort, page, minPrice, maxPrice, featuredOnly]);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value && value !== 'all' && value !== '') p.set(key, value); else p.delete(key);
    if (key !== 'page') p.delete('page');
    setSearchParams(p);
  };

  const updateMultipleParams = (updates) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v && v !== 'all' && v !== '') p.set(k, v); else p.delete(k);
    });
    p.delete('page');
    setSearchParams(p);
  };

  const selectPriceRange = (range) => {
    const updates = {};
    if (range.min > 0) updates.min_price = String(range.min); else updates.min_price = '';
    if (range.max !== null) updates.max_price = String(range.max); else updates.max_price = '';
    setCustomMin(updates.min_price);
    setCustomMax(updates.max_price);
    updateMultipleParams(updates);
  };

  const applyCustomPrice = () => {
    updateMultipleParams({ min_price: customMin, max_price: customMax });
  };

  const clearAllFilters = () => {
    setSearchParams({});
    setCustomMin('');
    setCustomMax('');
  };

  const addToCart = async (productId) => {
    if (!user) { navigate('/login'); return; }
    try {
      await axios.post(`${API}/cart/add`, { product_id: productId, quantity: 1 }, { withCredentials: true });
      toast.success('Added to cart');
    } catch { toast.error('Failed to add to cart'); }
  };

  const toggleWishlist = async (productId) => {
    if (!user) { navigate('/login'); return; }
    try {
      const { data } = await axios.post(`${API}/wishlist/toggle`, { product_id: productId }, { withCredentials: true });
      setWishlistIds(prev => data.action === 'added' ? [...prev, productId] : prev.filter(id => id !== productId));
      toast.success(data.message);
    } catch { toast.error('Failed to update wishlist'); }
  };

  const activeFilterCount = [
    category !== 'all',
    minPrice || maxPrice,
    featuredOnly,
    search,
  ].filter(Boolean).length;

  const isPriceRangeActive = (range) => {
    const curMin = minPrice ? parseInt(minPrice) : 0;
    const curMax = maxPrice ? parseInt(maxPrice) : null;
    return curMin === range.min && curMax === range.max;
  };

  // ── Sidebar Filter Panel ──
  const FilterPanel = () => (
    <div className="font-body" data-testid="filter-panel">
      {/* Active filters summary */}
      {activeFilterCount > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', background: 'rgba(196,94,42,0.06)', borderRadius: '12px', marginBottom: '16px',
        }}>
          <span style={{ fontSize: '0.82rem', color: '#C45E2A', fontWeight: 600 }}>
            {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
          </span>
          <button onClick={clearAllFilters} style={{
            fontSize: '0.78rem', color: '#C45E2A', fontWeight: 500, cursor: 'pointer',
            background: 'none', border: 'none', textDecoration: 'underline',
          }}>Clear All</button>
        </div>
      )}

      {/* ── Category Filter ── */}
      <div style={{ borderBottom: '1px solid rgba(196,94,42,0.08)', paddingBottom: '16px', marginBottom: '16px' }}>
        <button onClick={() => setCatOpen(!catOpen)} style={{
          display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', background: 'none', border: 'none', padding: 0,
        }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1C1209', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag style={{ width: '14px', height: '14px', color: '#C45E2A' }} /> Category
          </span>
          {catOpen ? <ChevronUp style={{ width: '16px', height: '16px', color: '#8B6A52' }} /> : <ChevronDown style={{ width: '16px', height: '16px', color: '#8B6A52' }} />}
        </button>
        {catOpen && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
              borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s',
              background: category === 'all' ? 'rgba(196,94,42,0.08)' : 'transparent',
            }}>
              <input type="radio" name="category" checked={category === 'all'} onChange={() => updateParam('category', 'all')}
                style={{ accentColor: '#C45E2A', width: '16px', height: '16px' }} />
              <span style={{ fontSize: '0.85rem', color: category === 'all' ? '#C45E2A' : '#4A3728', fontWeight: category === 'all' ? 600 : 400 }}>
                All Categories
              </span>
            </label>
            {categories.map(c => (
              <label key={c} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                borderRadius: '10px', cursor: 'pointer', transition: 'all 0.15s',
                background: category === c ? 'rgba(196,94,42,0.08)' : 'transparent',
              }}>
                <input type="radio" name="category" checked={category === c} onChange={() => updateParam('category', c)}
                  style={{ accentColor: '#C45E2A', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '0.85rem', color: category === c ? '#C45E2A' : '#4A3728', fontWeight: category === c ? 600 : 400 }}>
                  {c}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* ── Price Filter ── */}
      <div style={{ borderBottom: '1px solid rgba(196,94,42,0.08)', paddingBottom: '16px', marginBottom: '16px' }}>
        <button onClick={() => setPriceOpen(!priceOpen)} style={{
          display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', background: 'none', border: 'none', padding: 0,
        }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1C1209', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <IndianRupee style={{ width: '14px', height: '14px', color: '#C45E2A' }} /> Price
          </span>
          {priceOpen ? <ChevronUp style={{ width: '16px', height: '16px', color: '#8B6A52' }} /> : <ChevronDown style={{ width: '16px', height: '16px', color: '#8B6A52' }} />}
        </button>
        {priceOpen && (
          <div style={{ marginTop: '12px' }}>
            {/* Quick price ranges */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '14px' }}>
              {PRICE_RANGES.map((range, i) => (
                <button key={i} onClick={() => selectPriceRange(range)} style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                  borderRadius: '10px', cursor: 'pointer', border: 'none', textAlign: 'left',
                  background: isPriceRangeActive(range) ? 'rgba(196,94,42,0.08)' : 'transparent',
                  transition: 'all 0.15s',
                }}>
                  <div style={{
                    width: '16px', height: '16px', borderRadius: '4px',
                    border: isPriceRangeActive(range) ? '2px solid #C45E2A' : '2px solid #C4B5A5',
                    background: isPriceRangeActive(range) ? '#C45E2A' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {isPriceRangeActive(range) && <span style={{ color: '#fff', fontSize: '10px', fontWeight: 700 }}>✓</span>}
                  </div>
                  <span style={{
                    fontSize: '0.85rem', color: isPriceRangeActive(range) ? '#C45E2A' : '#4A3728',
                    fontWeight: isPriceRangeActive(range) ? 600 : 400,
                  }}>{range.label}</span>
                </button>
              ))}
            </div>

            {/* Custom price range */}
            <div style={{ background: '#FFFDF9', borderRadius: '12px', padding: '14px', border: '1px solid rgba(196,94,42,0.06)' }}>
              <p style={{ fontSize: '0.78rem', fontWeight: 600, color: '#8B6A52', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Custom Range</p>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <Input value={customMin} onChange={e => setCustomMin(e.target.value.replace(/\D/g, ''))}
                  placeholder="₹ Min" inputMode="numeric" className="bg-white border-border-custom h-9 text-sm"
                  style={{ flex: 1 }} data-testid="price-min" />
                <span style={{ color: '#C4B5A5', fontSize: '0.85rem' }}>–</span>
                <Input value={customMax} onChange={e => setCustomMax(e.target.value.replace(/\D/g, ''))}
                  placeholder="₹ Max" inputMode="numeric" className="bg-white border-border-custom h-9 text-sm"
                  style={{ flex: 1 }} data-testid="price-max" />
              </div>
              <Button onClick={applyCustomPrice} size="sm"
                className="w-full mt-2 bg-rust hover:bg-rust/90 text-cream font-body rounded-full h-8 text-xs"
                data-testid="apply-price-btn">
                Apply
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ── Featured Filter ── */}
      <div style={{ borderBottom: '1px solid rgba(196,94,42,0.08)', paddingBottom: '16px', marginBottom: '16px' }}>
        <button onClick={() => setFeaturedOpen(!featuredOpen)} style={{
          display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', background: 'none', border: 'none', padding: 0,
        }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1C1209', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Star style={{ width: '14px', height: '14px', color: '#C45E2A' }} /> Featured
          </span>
          {featuredOpen ? <ChevronUp style={{ width: '16px', height: '16px', color: '#8B6A52' }} /> : <ChevronDown style={{ width: '16px', height: '16px', color: '#8B6A52' }} />}
        </button>
        {featuredOpen && (
          <div style={{ marginTop: '12px' }}>
            <label style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
              borderRadius: '10px', cursor: 'pointer',
              background: featuredOnly ? 'rgba(196,94,42,0.08)' : 'transparent',
            }}>
              <div onClick={() => updateParam('featured', featuredOnly ? '' : '1')} style={{
                width: '38px', height: '22px', borderRadius: '100px', cursor: 'pointer',
                background: featuredOnly ? '#C45E2A' : '#D5CBBD',
                transition: 'all 0.2s', position: 'relative', flexShrink: 0,
              }}>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: '2px',
                  left: featuredOnly ? '18px' : '2px',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                }} />
              </div>
              <span style={{ fontSize: '0.85rem', color: featuredOnly ? '#C45E2A' : '#4A3728', fontWeight: featuredOnly ? 600 : 400 }}>
                Show featured only
              </span>
            </label>
          </div>
        )}
      </div>

      {/* ── Sort ── */}
      <div>
        <button onClick={() => setSortOpen(!sortOpen)} style={{
          display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
          cursor: 'pointer', background: 'none', border: 'none', padding: 0,
        }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1C1209', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ArrowUpDown style={{ width: '14px', height: '14px', color: '#C45E2A' }} /> Sort By
          </span>
          {sortOpen ? <ChevronUp style={{ width: '16px', height: '16px', color: '#8B6A52' }} /> : <ChevronDown style={{ width: '16px', height: '16px', color: '#8B6A52' }} />}
        </button>
        {sortOpen && (
          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {SORT_OPTIONS.map(opt => (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
                borderRadius: '10px', cursor: 'pointer',
                background: sort === opt.value ? 'rgba(196,94,42,0.08)' : 'transparent',
              }}>
                <input type="radio" name="sort" checked={sort === opt.value} onChange={() => updateParam('sort', opt.value)}
                  style={{ accentColor: '#C45E2A', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '0.85rem', color: sort === opt.value ? '#C45E2A' : '#4A3728', fontWeight: sort === opt.value ? 600 : 400 }}>
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream" data-testid="products-page">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1C1209 0%, #3A2510 100%)',
        padding: '40px 0 36px',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-60px', right: '-60px',
          width: '250px', height: '250px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,140,63,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
          <h1 className="font-heading" style={{
            fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)', fontWeight: 800,
            color: '#FFF8F0', marginBottom: '6px',
          }}>
            {category !== 'all' ? category : 'All Products'}
          </h1>
          <p className="font-body" style={{ fontSize: '0.88rem', color: 'rgba(255,248,240,0.5)' }}>
            {loading ? 'Loading products...' : error ? 'Error loading products' : `${total} product${total !== 1 ? 's' : ''} found`}
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1600px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Top bar: search + mobile filter toggle */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: '#8B6A52' }} />
            <Input
              value={search} onChange={(e) => updateParam('search', e.target.value)}
              placeholder="Search products..."
              className="pl-10 bg-white border-border-custom h-11 font-body" data-testid="product-search-input"
            />
            {search && (
              <button onClick={() => updateParam('search', '')} style={{
                position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer',
              }}>
                <X style={{ width: '16px', height: '16px', color: '#8B6A52' }} />
              </button>
            )}
          </div>
          {/* Mobile filter button */}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden" style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '0 20px', height: '44px', borderRadius: '100px',
            background: sidebarOpen ? '#C45E2A' : '#fff',
            color: sidebarOpen ? '#fff' : '#4A3728',
            border: sidebarOpen ? 'none' : '1px solid rgba(196,94,42,0.15)',
            cursor: 'pointer', fontWeight: 500, fontSize: '0.88rem',
            transition: 'all 0.2s',
          }} data-testid="mobile-filter-toggle">
            <Filter style={{ width: '16px', height: '16px' }} />
            Filters
            {activeFilterCount > 0 && (
              <span style={{
                background: sidebarOpen ? 'rgba(255,255,255,0.3)' : '#C45E2A',
                color: sidebarOpen ? '#fff' : '#fff',
                width: '20px', height: '20px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 700,
              }}>{activeFilterCount}</span>
            )}
          </button>
        </div>

        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
          {/* ── Desktop Sidebar ── */}
          <aside className="hidden lg:block" style={{
            width: '230px', flexShrink: 0,
            background: '#fff', borderRadius: '20px',
            border: '1px solid rgba(196,94,42,0.08)',
            padding: '20px 18px',
            position: 'sticky', top: '82px',
            maxHeight: 'calc(100vh - 100px)', overflowY: 'auto',
            boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
          }} data-testid="desktop-filters">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
              <SlidersHorizontal style={{ width: '16px', height: '16px', color: '#C45E2A' }} />
              <span className="font-heading" style={{ fontSize: '1rem', fontWeight: 700, color: '#1C1209' }}>Filters</span>
            </div>
            <FilterPanel />
          </aside>

          {/* ── Mobile Sidebar Overlay ── */}
          {sidebarOpen && (
            <>
              <div onClick={() => setSidebarOpen(false)} className="lg:hidden" style={{
                position: 'fixed', inset: 0, background: 'rgba(28,18,9,0.4)',
                zIndex: 40, backdropFilter: 'blur(4px)',
              }} />
              <div className="lg:hidden" style={{
                position: 'fixed', left: 0, top: 0, bottom: 0,
                width: '300px', maxWidth: '85vw',
                background: '#fff', zIndex: 50,
                padding: '20px', overflowY: 'auto',
                boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
                animation: 'slideIn 0.3s ease',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SlidersHorizontal style={{ width: '16px', height: '16px', color: '#C45E2A' }} />
                    <span className="font-heading" style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1C1209' }}>Filters</span>
                  </div>
                  <button onClick={() => setSidebarOpen(false)} style={{
                    background: '#F5E8D8', border: 'none', borderRadius: '50%',
                    width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}>
                    <X style={{ width: '16px', height: '16px', color: '#1C1209' }} />
                  </button>
                </div>
                <FilterPanel />
              </div>
            </>
          )}

          {/* ── Product Grid ── */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Category Pills (quick access) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
              <button onClick={() => updateParam('category', 'all')}
                className="font-body"
                style={{
                  padding: '7px 18px', borderRadius: '100px', fontSize: '0.82rem', fontWeight: 500,
                  cursor: 'pointer', transition: 'all 0.2s', border: 'none',
                  background: category === 'all' ? '#C45E2A' : '#fff',
                  color: category === 'all' ? '#fff' : '#4A3728',
                  boxShadow: category === 'all' ? '0 3px 12px rgba(196,94,42,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
                }}
                data-testid="category-pill-all">All</button>
              {categories.map(c => (
                <button key={c} onClick={() => updateParam('category', c)}
                  className="font-body"
                  style={{
                    padding: '7px 18px', borderRadius: '100px', fontSize: '0.82rem', fontWeight: 500,
                    cursor: 'pointer', transition: 'all 0.2s', border: 'none',
                    background: category === c ? '#C45E2A' : '#fff',
                    color: category === c ? '#fff' : '#4A3728',
                    boxShadow: category === c ? '0 3px 12px rgba(196,94,42,0.3)' : '0 1px 4px rgba(0,0,0,0.06)',
                  }}
                  data-testid={`category-pill-${c.toLowerCase().replace(/\s+/g, '-')}`}>{c}</button>
              ))}
            </div>

            {/* Active filter chips */}
            {(search || minPrice || maxPrice || featuredOnly || category !== 'all') && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                {search && (
                  <span className="font-body" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '5px 14px', borderRadius: '100px', fontSize: '0.78rem',
                    background: '#F5E8D8', color: '#C45E2A', fontWeight: 500,
                  }}>
                    Search: "{search}"
                    <button onClick={() => updateParam('search', '')} style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: 0 }}>
                      <X style={{ width: '12px', height: '12px' }} />
                    </button>
                  </span>
                )}
                {(minPrice || maxPrice) && (
                  <span className="font-body" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '5px 14px', borderRadius: '100px', fontSize: '0.78rem',
                    background: '#F5E8D8', color: '#C45E2A', fontWeight: 500,
                  }}>
                    Price: {minPrice ? `₹${parseInt(minPrice).toLocaleString('en-IN')}` : '₹0'} – {maxPrice ? `₹${parseInt(maxPrice).toLocaleString('en-IN')}` : 'Any'}
                    <button onClick={() => { updateMultipleParams({ min_price: '', max_price: '' }); setCustomMin(''); setCustomMax(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: 0 }}>
                      <X style={{ width: '12px', height: '12px' }} />
                    </button>
                  </span>
                )}
                {featuredOnly && (
                  <span className="font-body" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '5px 14px', borderRadius: '100px', fontSize: '0.78rem',
                    background: '#F5E8D8', color: '#C45E2A', fontWeight: 500,
                  }}>
                    Featured only
                    <button onClick={() => updateParam('featured', '')} style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: 0 }}>
                      <X style={{ width: '12px', height: '12px' }} />
                    </button>
                  </span>
                )}
              </div>
            )}

            {/* Products Grid */}
            {error ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '16px', color: '#dc2626' }}>⚠️</div>
                <p className="font-body" style={{ fontSize: '1.1rem', color: '#8B6A52', marginBottom: '8px' }}>Failed to load products</p>
                <p className="font-body" style={{ fontSize: '0.85rem', color: '#C4B5A5', marginBottom: '20px' }}>{error}</p>
                <Button onClick={() => window.location.reload()} variant="outline" className="font-body border-rust text-rust rounded-full">Retry</Button>
              </div>
            ) : loading && products.length === 0 ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><div className="spinner"></div></div>
            ) : products.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <div style={{ fontSize: '4rem', marginBottom: '16px' }}>🐱</div>
                <p className="font-body" style={{ fontSize: '1.1rem', color: '#8B6A52', marginBottom: '8px' }}>No products found</p>
                <p className="font-body" style={{ fontSize: '0.85rem', color: '#C4B5A5', marginBottom: '20px' }}>Try adjusting your filters or search</p>
                <Button onClick={clearAllFilters} variant="outline" className="font-body border-rust text-rust rounded-full">Clear All Filters</Button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                {loading && (
                  <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '60px', background: 'rgba(255,253,249,0.5)' }}>
                    <div className="spinner"></div>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="products-grid" style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                  {products.map(p => (
                    <ProductCard key={p.id} product={p} onAddToCart={addToCart} onToggleWishlist={toggleWishlist} isWishlisted={wishlistIds.includes(p.id)} />
                  ))}
                </div>
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '48px' }}>
                {page > 1 && (
                  <button onClick={() => updateParam('page', page - 1)} className="font-body" style={{
                    padding: '8px 18px', borderRadius: '100px', fontSize: '0.85rem',
                    background: '#fff', color: '#4A3728', border: '1px solid rgba(196,94,42,0.15)',
                    cursor: 'pointer', fontWeight: 500,
                  }}>← Prev</button>
                )}
                {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => updateParam('page', p)}
                    className="font-body"
                    style={{
                      width: '40px', height: '40px', borderRadius: '50%', fontSize: '0.85rem',
                      background: p === page ? '#C45E2A' : '#fff',
                      color: p === page ? '#fff' : '#4A3728',
                      border: p === page ? 'none' : '1px solid rgba(196,94,42,0.15)',
                      cursor: 'pointer', fontWeight: p === page ? 700 : 400,
                      boxShadow: p === page ? '0 3px 12px rgba(196,94,42,0.3)' : 'none',
                    }}
                    data-testid={`page-${p}`}>{p}</button>
                ))}
                {page < pages && (
                  <button onClick={() => updateParam('page', page + 1)} className="font-body" style={{
                    padding: '8px 18px', borderRadius: '100px', fontSize: '0.85rem',
                    background: '#fff', color: '#4A3728', border: '1px solid rgba(196,94,42,0.15)',
                    cursor: 'pointer', fontWeight: 500,
                  }}>Next →</button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile sidebar slide-in animation */}
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
